import { useEffect, useState } from 'react'

import {
  InnosErrorBoundary,
  emitContainerEvent
} from '@inno-tools/container-bridge'
import cn from 'clsx'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { useMountApp } from 'modules/app-registry/hooks/useMountApp'
import { ErrorPage } from 'modules/app-registry/pages/ErrorPage'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation } from 'react-router-dom'
import { useReportDetachedAppLoadState } from 'shared/components/AppFocusBroadcaster/useReportDetachedAppLoadState'
import { NavigationContextProvider } from 'shared/contexts/Navigation'
import { useUserContext } from 'shared/hooks'

import styles from './FullPageAppTemplate.module.scss'
import { FullPageAppTemplateProps } from './FullPageAppTemplate.types'
import { getStateContent } from './FullPageAppTemplate.utils'
import { AppLoader } from './components'

type AppLoadErrorPageProps = {
  appName?: string
  missingRemoteUrl?: boolean
}

const AppLoadErrorPage = ({
  appName,
  missingRemoteUrl = false
}: AppLoadErrorPageProps) => {
  const { t } = useTranslation()
  const errorKey = missingRemoteUrl
    ? 'errors.ENV_NOT_SET'
    : 'errors.COMPONENT_NOT_LOADED'

  return (
    <ErrorPage
      title={t(`${errorKey}.title`, {
        appName: appName || t('errors.DEFAULT_APP_NAME')
      })}
      secondLine={t(`${errorKey}.secondLine`)}
      description={t(`${errorKey}.description`)}
      retryBtnLabel={t('errors.RETRY_BTN_LABEL')}
    />
  )
}

const FullPageAppTemplateContent = (props: FullPageAppTemplateProps) => {
  const {
    className,
    appProps,
    appName,
    url,
    scope,
    entryPath,
    envKey,
    cdnUrl,
    isLoaded: isLoadedProp = true,
    hasFullHeight = false,
    skipPermissionCheck
  } = props
  const { user } = useUserContext()

  const location = useLocation()
  const loadStateAppId = props.appId ?? props.scope
  const isPublicRoute =
    location.pathname.includes('/public') ||
    location.pathname.includes('/guest')

  useEffect(() => {
    const initTitle = document.title
    // Need to change the document.title
    // to make analytics show relevant statistics
    document.title = `${initTitle} | ${appName}`

    return () => {
      document.title = initTitle
    }
  }, [appName])

  const isAuthRequired = !isPublicRoute && !skipPermissionCheck

  const { ref, isLoaded, isError, missingRemoteUrl } = useMountApp(
    {
      url,
      scope,
      entryPath,
      envKey,
      cdnUrl
    },
    appProps,
    isAuthRequired
  )

  useReportDetachedAppLoadState({
    appId: loadStateAppId,
    isLoaded,
    isError,
    missingRemoteUrl
  })

  if (!user && isAuthRequired) {
    return <Navigate to='/login' replace />
  }

  return (
    <NavigationContextProvider>
      {getStateContent(
        { isLoaded: isLoaded && isLoadedProp, isError },
        <AppLoadErrorPage
          appName={appName}
          missingRemoteUrl={missingRemoteUrl}
        />
      )}
      <div
        className={cn(
          className,
          styles.fullPageAppTemplate,
          (!isLoaded || !isLoadedProp) && styles.hideContent,
          hasFullHeight && styles.fullHeight,
          isPublicRoute && styles.publicRoute
        )}
        ref={ref}
      />
    </NavigationContextProvider>
  )
}

const FullPageAppTemplate = (props: FullPageAppTemplateProps) => {
  const [shouldRender, setShouldRender] = useState(true)

  const { apps, isLoading } = useAvailableApps()

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'UNMOUNT_APP') {
        setShouldRender(false)
      }
    }

    window.addEventListener('message', handleMessage)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const container = (window as any)?.['__CONTAINER__']
    if (container?.parentOrigin) {
      window.parent.postMessage(
        {
          type: 'APP_READY',
          windowId: container.windowId
        },
        container.parentOrigin
      )
    }

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  useEffect(() => {
    if (!shouldRender) {
      emitContainerEvent({
        type: 'CLOSE_WINDOW',
        data: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          windowId: (window as any)?.['__CONTAINER__']?.windowId
        }
      })
    }
  }, [shouldRender])

  const canAccessApp = props.skipPermissionCheck
    ? true
    : apps?.[props.appId] && !apps[props.appId].isDisabled

  if (!shouldRender || isLoading) {
    return null
  }

  if (!canAccessApp) {
    return <Navigate to='/' replace />
  }

  return (
    <InnosErrorBoundary
      debugName={`FullPageAppTemplate-${props.appName}`}
      loadingComponent={<AppLoader />}
      fallbackComponent={() => <AppLoadErrorPage appName={props.appName} />}
    >
      <FullPageAppTemplateContent {...props} />
    </InnosErrorBoundary>
  )
}

FullPageAppTemplate.displayName = 'FullPageAppTemplate'

export { FullPageAppTemplate }
